import { apiClient } from "@/api/api-client.ts";

interface Identifiable {
	id?: string;
}

export interface Pagination extends Record<string, any> {
	current_page: number;
	from: number;
	last_page: number;
	per_page: number;
	to: number;
	total: number;
	status_count: any;
}

export interface InfiniteQueryData<T> {
	pageParams: number[];
	pages: {
		data: T;
		meta: Pagination;
	}[];
}

type BaseService<T extends Identifiable> = {
	list: (params?: any) => Promise<{ data: T[]; meta: Pagination }>;
	findById: (id: string) => Promise<T>;
	findByIdWithInclude: (params: {
		id: string;
		include?: string;
	}) => Promise<T>;
	findByIdWithParameter: (params: {
		id: string;
		parameters?: Record<string, string | number>;
	}) => Promise<T>;
	create: (data: T) => Promise<T>;
	updateById: (id: string, data: T) => Promise<T>;
	updateByIdEmail: (id: string, data: T) => Promise<T>;
	upsert: (data: T) => Promise<T>;
	deleteById: (id: string) => Promise<void>;
};

function createBaseService<T extends Identifiable>(baseUrl: string, identifier: keyof T = "id"): BaseService<T> {
	return {
		list: async (params = {}) => {
			const response = await apiClient.get<{ data: T[]; meta: Pagination }>(`${baseUrl}`, { params });

			return response.data;
		},

		findById: async (id: string) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${id}`);

			return response.data.data;
		},

		findByIdWithInclude: async (params: { id: string; include?: string }) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${params.id}`, {
				params: { include: params.include },
			});

			return response.data.data;
		},

		findByIdWithParameter: async (params: {
			id: string;
			parameters?: Record<string, string | number>;
		}) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${params.id}`, {
				params: {
					...params.parameters,
				},
			});

			return response.data.data;
		},

		create: async (data: T) => {
			const response = await apiClient.post<{ data: T }>(baseUrl, data);
			return response.data.data;
		},

		updateById: async (id: string, data: T) => {
			const response = await apiClient.put<{ data: T }>(`${baseUrl}/${id}`, data);
			return response.data.data;
		},

		deleteById: async (id: string) => {
			await apiClient.delete(`${baseUrl}/${id}`);
		},

		upsert: async (data: T) => {
			const id = data[identifier];
			if (id) {
				const response = await apiClient.put<{ data: T }>(`${baseUrl}/${id}`, data);
				return response.data.data;
			}

			const response = await apiClient.post<{ data: T }>(baseUrl, data);

			return response.data.data;
		},
	};
}

export default createBaseService;
